import React from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {useParams, Redirect} from 'react-router-dom'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePage, useShopperContext, useUsid} from '@salesforce/commerce-sdk-react'
import {Page} from '@salesforce/commerce-sdk-react/components'
import useSiteType from '../../hooks/use-site-type'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@app/hooks/use-multi-site'

import {
    PDCLink,
    PDCHomeServicesPushTile,
    PDCHomeCategoriesPushTile,
    PDCInnerAccordion,
    PDCFooterReinsuranceIcon,
    PDCFooterPaymentIcon,
    PDCBlogSubmenu,
    PDCSubmenu,
    PDCBlogArticlesList,
    PDCStyleGuideTile,
    PDCDeliveryOption,
    PDCHomeSEO,
    PDCCollaborationPushTile,
    PDCAboutIconicBlockTile,
    PDCCarouselImageGalleryTile,
    PDCShippingPDPDetailsPD
} from '../../page-designer/assets'
import {
    PDBottomPLPLayout,
    PDCHomeDyptich,
    PDCHomeFullScreenPushImage,
    PDCHomeFullScreenPushVideo,
    PDCHomeProductDyptichVideo,
    PDCHomeProductDyptichImage,
    PDCHomeServicesPush,
    PDCHomeCategoriesPush,
    PDCAboutTextBlock,
    PDCButton,
    PDCTitlePageOnHeaderImage,
    PDCAnchorsBlock,
    PDCAccordionBlock,
    PDCAccordion,
    PDCFooterLinksListMobile,
    PDCFooterLinksListDesktop,
    PDCFooterLinksListCheckout,
    PDCFooterPayments,
    PDCFooterReinsurance,
    PDCAboutStoryBlockDefine,
    PDCAboutStoryBlockExcerpt,
    PDCBespokeContactForm,
    PDCHomeHeroArticle,
    PDCCookiesRadios,
    PDCDoNotSellForm,
    PDCContactUsForm,
    PDCClassicProductCarousel,
    PDCTrackMyOrderForm,
    PDCStyleGuideCarousel,
    PDCDeliveryOptionsContainer,
    PDCCollaborationPush,
    PDCHomePatternFinderPush,
    PDCHomeAboutPush,
    PDCAboutIconicBlock,
    PDCAboutProductSlider,
    PDCTextBlock,
    PDCCarouselImageGallery,
    PDCShippingPDPDetailsLayout
} from '../../page-designer/layouts'

// Constants
import {
    MAX_CACHE_AGE_STATIC_PAGES,
    STALE_WHILE_REVALIDATE,
    GIFTCARD_PDC_PAGE_ID,
    PAGE_NOT_FOUND_PDC_PAGE_ID
} from '../../constants'

import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Utils
import {getEnvStaleTime} from '../../utils/utils'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

// Custom hooks
import {useGTM} from '@app/hooks/use-gtm'
import {PDPPersonalizePush} from '../../components/product-view/personalizePush'
import {PDCAboutStoryLinksBlock} from '../../page-designer/layouts/pdc-about-story-links-block'
import {PDCAboutStoryLinksBlockTile} from '../../page-designer/assets/pdc-about-story-links-block-tile'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcLink': PDCLink,
    'commerce_assets.pdcDisplayConfigLink': PDCLink,
    'commerce_assets.pdcHomeServicesPushTile': PDCHomeServicesPushTile,
    'commerce_assets.pdcHomeCategoriesPushTile': PDCHomeCategoriesPushTile,
    'commerce_assets.pdcInnerAccordion': PDCInnerAccordion,
    'commerce_assets.pdcFooterPaymentIcon': PDCFooterPaymentIcon,
    'commerce_assets.pdcFooterReinsuranceIcon': PDCFooterReinsuranceIcon,
    'commerce_assets.pdcBlogSubmenu': PDCBlogSubmenu,
    'commerce_assets.pdcSubmenu': PDCSubmenu,
    'commerce_assets.pdcBlogArticlesList': PDCBlogArticlesList,
    'commerce_assets.pdcStyleGuideTile': PDCStyleGuideTile,
    'commerce_assets.pdcDeliveryOptions': PDCDeliveryOption,
    'commerce_assets.pdcHomeSeo': PDCHomeSEO,
    'commerce_assets.pdcPdpCollaborationPushTile': PDCCollaborationPushTile,
    'commerce_assets.pdcAboutStoryLinksBlockTile': PDCAboutStoryLinksBlockTile,
    'commerce_assets.pdcAboutIconicBlockTile': PDCAboutIconicBlockTile,
    'commerce_assets.pdcCarouselImageGalleryTile': PDCCarouselImageGalleryTile,
    'commerce_assets.pdcShippingPDP': PDCShippingPDPDetailsPD,
    'commerce_layouts.pdBottomPLPLayout': PDBottomPLPLayout,
    'commerce_layouts.pdcHomeDyptich': PDCHomeDyptich,
    'commerce_layouts.pdcHomeFullScreenPushImage': PDCHomeFullScreenPushImage,
    'commerce_layouts.pdcHomeFullScreenPushVideo': PDCHomeFullScreenPushVideo,
    'commerce_layouts.pdcHomeProductDyptichVideo': PDCHomeProductDyptichVideo,
    'commerce_layouts.pdcHomeProductDyptichImage': PDCHomeProductDyptichImage,
    'commerce_layouts.pdcHomeServicesPush': PDCHomeServicesPush,
    'commerce_layouts.pdcHomeCategoriesPush': PDCHomeCategoriesPush,
    'commerce_layouts.pdcAboutTextBlock': PDCAboutTextBlock,
    'commerce_layouts.pdcButton': PDCButton,
    'commerce_layouts.pdcFooterLinksListMobile': PDCFooterLinksListMobile,
    'commerce_layouts.pdcFooterLinksListDesktop': PDCFooterLinksListDesktop,
    'commerce_layouts.pdcFooterLinksListCheckout': PDCFooterLinksListCheckout,
    'commerce_layouts.pdcFooterPayments': PDCFooterPayments,
    'commerce_layouts.pdcFooterReinsurance': PDCFooterReinsurance,
    'commerce_layouts.pdcTitlePageOnHeaderImage': PDCTitlePageOnHeaderImage,
    'commerce_layouts.pdcAnchorsBlock': PDCAnchorsBlock,
    'commerce_layouts.pdcAccordionBlock': PDCAccordionBlock,
    'commerce_layouts.pdcAccordion': PDCAccordion,
    'commerce_layouts.pdcBespokeContactForm': PDCBespokeContactForm,
    'commerce_layouts.pdcCookiesRadios': PDCCookiesRadios,
    'commerce_layouts.pdcAboutStoryBlockDefine': PDCAboutStoryBlockDefine,
    'commerce_layouts.pdcAboutStoryBlockExcerpt': PDCAboutStoryBlockExcerpt,
    'commerce_layouts.pdcHomeHeroArticle': PDCHomeHeroArticle,
    'commerce_layouts.pdcDoNotSellForm': PDCDoNotSellForm,
    'commerce_layouts.pdcContactUsForm': PDCContactUsForm,
    'commerce_layouts.pdcClassicProductCarousel': PDCClassicProductCarousel,
    'commerce_layouts.pdcTrackMyOrderForm': PDCTrackMyOrderForm,
    'commerce_layouts.pdcStyleGuideCarousel': PDCStyleGuideCarousel,
    'commerce_layouts.pdcDeliveryOptionsContainer': PDCDeliveryOptionsContainer,
    'commerce_layouts.pdcPdpCollaborationPush': PDCCollaborationPush,
    'commerce_layouts.pdcHomePatternFinderPush': PDCHomePatternFinderPush,
    'commerce_layouts.pdcHomeAboutPush': PDCHomeAboutPush,
    'commerce_layouts.pdcPdpPersonalizePush': PDPPersonalizePush,
    'commerce_layouts.pdcAboutStoryLinksBlock': PDCAboutStoryLinksBlock,
    'commerce_layouts.pdcAboutIconicBlock': PDCAboutIconicBlock,
    'commerce_layouts.pdcAboutProductSlider': PDCAboutProductSlider,
    'commerce_layouts.pdcTextBlock': PDCTextBlock,
    'commerce_layouts.pdcCarouselImageGallery': PDCCarouselImageGallery,
    'commerce_layouts.pdcShippingPDPLayout': PDCShippingPDPDetailsLayout
}

const PageViewer = () => {
    const {pageId} = useParams()

    const {usid} = useUsid()
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const {data: shopperContext} = useShopperContext(
        {
            parameters: {usid: usid}
        },
        {enabled: Boolean(!isServer && usid)}
    )

    /**
     * These pages have their own routes and should not be rendered via this component.
     * If a page with one of these IDs is requested, a 404 page will be rendered.
     */
    const {isOutlet} = useSiteType()
    const {buildURLWithUTM} = useMultiSite()
    const BLACKLISTED_PAGE_IDS = [
        GIFTCARD_PDC_PAGE_ID,
        PAGE_NOT_FOUND_PDC_PAGE_ID + (isOutlet ? '-outlet' : '')
    ]

    const staleTime = getEnvStaleTime()
    const {triggerFaqAccordionClickEvent, triggerFaqLinkClickEvent} = useGTM()
    const {data: page, error} = usePage(
        {
            parameters: {
                pageId,
                c_country: shopperContext?.geoLocation?.countryCode,
                c_registered: isRegistered
            }
        },
        {
            staleTime: staleTime,
            enabled: !BLACKLISTED_PAGE_IDS.includes(pageId)
        }
    )

    // useServerContext is a special hook introduced in v3 PWA Kit SDK.
    // It replaces the legacy `getProps` and provide a react hook interface for SSR.
    // it returns the request and response objects on the server side,
    // and these objects are undefined on the client side.
    const {res} = useServerContext()

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_STATIC_PAGES}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    if (BLACKLISTED_PAGE_IDS.includes(pageId) || error) {
        return (
            <Redirect
                to={buildURLWithUTM(`/${PAGE_NOT_FOUND_PDC_PAGE_ID + (isOutlet ? '-outlet' : '')}`)}
            />
        )
    }

    /**
     * Handles the click event for FAQ items in the page viewer.
     *
     * This function is typically used to toggle the visibility of FAQ answers or to perform
     * additional actions when a user interacts with an FAQ entry. It may receive event data
     * or an identifier for the clicked FAQ item, depending on implementation.
     *
     * @param {Event|any} event - The event object or identifier associated with the FAQ click.
     * @returns {void}
     */
    const handleFaqClick = (event) => {
        let targetElement = event.target

        // "faq_sections_used" event -  an <li> tag within a <ul> with role="tablist"
        if (targetElement.tagName === 'LI') {
            let parentElement = targetElement.parentElement
            while (parentElement) {
                if (
                    parentElement.tagName === 'UL' &&
                    parentElement.getAttribute('role') === 'tablist'
                ) {
                    triggerFaqAccordionClickEvent()
                    return
                }
                parentElement = parentElement.parentElement
            }
        }

        while (targetElement && targetElement.tagName !== 'A') {
            targetElement = targetElement.parentElement
        }
        if (targetElement && targetElement.tagName === 'A') {
            let parentElement = targetElement.parentElement
            while (parentElement) {
                if (parentElement.getAttribute('role') === 'tabpanel') {
                    triggerFaqLinkClickEvent(targetElement.href, targetElement.textContent)
                    return
                }
                parentElement = parentElement.parentElement
            }
        }
    }

    return (
        <>
            <Box
                layerStyle={'page'}
                css={[PageViewer.css]}
                onClick={pageId === 'faq' ? handleFaqClick : undefined}
            >
                <Page page={page} components={PAGEDESIGNER_TO_COMPONENT} />
            </Box>
        </>
    )
}

PageViewer.css = css`
    // Avoid the header to overlap target component
    // when clicking on an anchor link
    .region > .container > .component {
        scroll-margin-top: 64px;

        @media (min-width: 768px) {
            scroll-margin-top: 52px;
        }
    }

    // To have good position of breadcrumbs on PD pages with all Header style types
    position: relative;
`

PageViewer.displayName = 'PageViewer'

export default PageViewer
